import React, { useEffect, useRef, useState } from 'react';
import { X, Calendar, Clock, MapPin } from 'lucide-react';
import './AssemblyNoticeModal.css';

/**
 * AssemblyNoticeModal — Convocatoria oficial de la Junta General Extraordinaria.
 *
 * Se muestra UNA vez por pestaña (sessionStorage):
 *  - Pestaña/ventana nueva  -> sessionStorage vacío -> se muestra.
 *  - Navegación interna (home -> sección -> home, o volver desde otra ruta) -> flag ya
 *    presente en la sesión -> NO se repite.
 *  - Recargar la misma pestaña mantiene el flag; abrir una pestaña nueva vuelve a mostrarlo,
 *    aunque pasen días.
 *
 * La clave va versionada con la fecha del acto: cambiarla reinicia el "visto" para un
 * futuro aviso.
 */
const STORAGE_KEY = 'whata-assembly-notice-2026-07-24';

const AssemblyNoticeModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef(null);

  // Decidir visibilidad al montar. Nueva pestaña => sin flag => se muestra.
  useEffect(() => {
    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // Storage no disponible (modo privado estricto, etc.): mostramos igualmente.
    }
    if (!alreadySeen) {
      setIsOpen(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* noop */
      }
    }
  }, []);

  const close = () => setIsOpen(false);

  // Mientras está abierto: Escape para cerrar, bloqueo del scroll de fondo y foco inicial.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="assembly-overlay" onClick={close}>
      <div
        className="assembly-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assembly-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="assembly-header">
          <span className="assembly-eyebrow">Comunicado oficial a los socios</span>
          <button
            type="button"
            className="assembly-close-btn"
            onClick={close}
            aria-label="Cerrar aviso"
          >
            <X size={22} />
          </button>
        </div>

        <div className="assembly-body">
          <div className="assembly-meta">
            <span className="assembly-meta__item">
              <Calendar size={16} /> 24 de julio de 2026
            </span>
            <span className="assembly-meta__item">
              <Clock size={16} /> 12:00 CET
            </span>
            <span className="assembly-meta__item">
              <MapPin size={16} /> Coworking Lexington · Madrid
            </span>
          </div>

          <h2 id="assembly-title" className="assembly-title">
            CONVOCATORIA DE LA JUNTA GENERAL EXTRAORDINARIA DE “WHATA HUB WEB3, SOCIEDAD LIMITADA”
          </h2>

          <p>Estimados Socios:</p>

          <p>
            En cumplimiento de lo dispuesto en los Estatutos Sociales de WHATA HUB WEB3, SOCIEDAD
            LIMITADA (en adelante, la “Sociedad”), y de conformidad con lo previsto en el Texto
            Refundido de la Ley de Sociedades de Capital, aprobado por el Real Decreto Legislativo
            1/2010, de 2 de julio (en adelante, “LSC”), D. Roberto Garrido Vázquez, en su calidad de
            Administrador Único de la Sociedad, convoca a los señores Socios a la Junta General
            Extraordinaria de la Sociedad, que se celebrará de manera presencial en el centro de
            negocios Coworking Lexington (sala María Callas), sito en Calle Príncipe de Vergara 132,
            28002, Madrid, el próximo día 24 de julio de 2026, a las 12:00 CET horas, al objeto de
            deliberar y resolver sobre los asuntos comprendidos en el siguiente
          </p>

          <h3 className="assembly-orden-title">ORDEN DEL DÍA</h3>

          <ol className="assembly-orden">
            <li>
              Examen y aprobación, si procede, de las cuentas anuales de la Sociedad correspondientes al
              ejercicio cerrado a 31 de diciembre de 2025.
            </li>
            <li>
              Examen y aprobación, si procede, de la aplicación del resultado del ejercicio social
              finalizado el 31 de diciembre de 2025.
            </li>
            <li>
              Examen y aprobación, si procede, de la gestión social llevada a cabo por los Administrador
              único del ejercicio finalizado el 31 de diciembre de 2025.
            </li>
            <li>
              Información y deliberación sobre la situación económica y financiera actual de la Sociedad y
              el estado de sus actividades de negocio.
            </li>
            <li>
              Con carácter alternativo al punto anterior, examen, deliberación y, en su caso, adopción del
              acuerdo de disolución de la Sociedad al amparo de lo previsto en los artículos 363 y
              siguientes y 368 de la LSC.
            </li>
            <li>
              Para el supuesto de que se apruebe la disolución conforme al punto anterior, y de
              conformidad con lo dispuesto en los artículos 374, 376 y siguientes de la LSC, cese del
              Administrador Único como consecuencia del acuerdo de disolución y determinación del número,
              nombramiento y facultades del liquidador o liquidadores de la Sociedad.
            </li>
            <li>
              A propuesta del socio D. Fénix Moca S.L: Examen, deliberación y, en su caso, adopción de
              acuerdos sobre el cese del actual administrador y CEO de la Sociedad y el nombramiento de un
              nuevo administrador y CEO.
            </li>
            <li>Ruegos y preguntas.</li>
            <li>Redacción, lectura y aprobación del acta de la reunión.</li>
          </ol>

          <p>
            La Junta se celebrará en un lugar distinto del domicilio social debido a que las instalaciones
            disponibles en éste no permiten albergar adecuadamente el número previsible de asistentes,
            garantizando así el normal desarrollo de la reunión
          </p>

          <p>
            De conformidad con el artículo 196 de la LSC, a partir de la remisión de la presente
            convocatoria los señores Socios podrán solicitar por escrito al órgano de administración, con
            anterioridad a la celebración de la Junta, la información o aclaraciones que estimen necesarias
            en relación con los asuntos comprendidos en el orden del día. Asimismo, en relación con el
            punto primero del orden del día y de conformidad con el artículo 272.2 de la LSC, cualquier
            Socio podrá obtener de la Sociedad, de forma inmediata y gratuita, los documentos que han de
            ser sometidos a la aprobación de la Junta.
          </p>

          <p>
            Los señores Socios podrán hacerse representar en la Junta en los términos del artículo 183 de
            la LSC, debiendo conferirse la representación por escrito y con carácter especial para la
            presente reunión.
          </p>

          <p>Sin otro particular, reciban un cordial saludo.</p>

          <p>En Madrid, a 9 de julio de 2026.</p>

          <p>Atentamente,</p>

          <div className="assembly-signature">
            <span className="assembly-signature__line">______________________________</span>
            <span>D. Roberto Garrido Vázquez</span>
            <span>Administrador Único de WHATA HUB WEB3, S.L.</span>
          </div>
        </div>

        <div className="assembly-footer">
          <button type="button" className="assembly-accept-btn" onClick={close}>
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssemblyNoticeModal;
